// src/components/Callback.js
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import "./Callback.css";
import logger from "../utils/logger";

const Callback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleCallback } = useAuth();
  const [error, setError] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const hasProcessed = useRef(false);

  useEffect(() => {
    const processCallback = async () => {
      // Prevent double execution in React.StrictMode
      if (hasProcessed.current) {
        return;
      }

      hasProcessed.current = true;

      try {
        // Parse query parameters from URL
        const params = new URLSearchParams(location.search);
        const code = params.get("code");
        const state = params.get("state");
        const errorParam = params.get("error");
        const errorDescription = params.get("error_description");

        // Check for OAuth error response
        if (errorParam) {
          setError(errorDescription || `Authentication error: ${errorParam}`);
          // Redirect to login after showing error
          setTimeout(() => navigate("/login", { replace: true }), 3000);
          return;
        }

        // Validate required parameters
        if (!code || !state) {
          setError(
            "Invalid callback parameters. Missing authorization code or state."
          );
          setTimeout(() => navigate("/login", { replace: true }), 3000);
          return;
        }

        // Handle the OAuth callback
        const success = await handleCallback(code, state);

        if (success) {
          // Set navigating state to keep loading screen visible during transition
          setIsNavigating(true);
          // Navigate after a brief moment to ensure smooth transition
          setTimeout(() => {
            navigate("/", { replace: true });
          }, 100);
        } else {
          // Error already set by handleCallback
          setTimeout(() => navigate("/login", { replace: true }), 3000);
        }
      } catch (err) {
        logger.error("Callback processing error:", err);
        setError("An unexpected error occurred during authentication.");
        setTimeout(() => navigate("/login", { replace: true }), 3000);
      }
    };

    processCallback();
  }, [location, handleCallback, navigate]);

  return (
    <div className="callback-container">
      <div className="callback-content">
        {error ? (
          <>
            <div className="callback-error-icon">⚠️</div>
            <h2>Authentication Error</h2>
            <p className="callback-error-message">{error}</p>
            <p className="callback-redirect-message">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="callback-spinner"></div>
            <h2>
              {isNavigating
                ? "Loading Application..."
                : "Completing Authentication"}
            </h2>
            <p>
              {isNavigating
                ? "Setting up your workspace..."
                : "Please wait while we securely log you in..."}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default Callback;
